import { useCallback, useEffect, useState } from "react";
import { InlineHelp } from "@/components/common/InlineHelp";
import { AudienceDataDealerMapCard } from "@/components/audience-data/AudienceDataDealerMapCard";
import { OpsLocationsMappedList } from "./OpsLocationsMappedList";
import {
  fetchSsdDealerMap,
  mapSsdDealer,
  unmapSsdDealer,
  type SsdDealerMapBoard,
  type SsdMappedDealer,
} from "@/lib/ssdSalesClient";

const EMPTY: SsdDealerMapBoard = {
  unmapped: [],
  mapped: [],
  subscribers: [],
  can_release: false,
  manufacturer_org_id: null,
  manufacturer_name: null,
};

export function OpsLocationsTab(props: { onChanged?: () => void }) {
  const { onChanged } = props;
  const [board, setBoard] = useState<SsdDealerMapBoard>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    const r = await fetchSsdDealerMap();
    if (!r.ok) {
      setMsg(r.error || "Unable to load host lot mappings.");
      return;
    }
    const next = r.data.dealer_map ?? EMPTY;
    setBoard({ ...EMPTY, ...next, mapped: next.mapped ?? [] });
    onChanged?.();
    setMsg("");
  }, [onChanged]);

  useEffect(() => {
    void load();
  }, [load]);

  async function onMap(input: Parameters<typeof mapSsdDealer>[0]): Promise<boolean> {
    setBusy(true);
    const r = await mapSsdDealer(input);
    setBusy(false);
    if (!r.ok) {
      setMsg(r.error || "Unable to save mapping.");
      return false;
    }
    await load();
    return true;
  }

  async function onRemap(row: SsdMappedDealer, sellingOrgId: string, locationId: string | null): Promise<boolean> {
    return onMap({
      source_dealer_id: row.source_dealer_id,
      source_dealer_name: row.source_dealer_name,
      selling_org_id: sellingOrgId,
      location_id: locationId,
    });
  }

  async function onUnmap(sourceDealerId: string): Promise<boolean> {
    setBusy(true);
    const r = await unmapSsdDealer(sourceDealerId);
    setBusy(false);
    if (!r.ok) {
      setMsg(r.error || "Unable to unmap that lot.");
      return false;
    }
    await load();
    return true;
  }

  const unmatched = board.unmapped.length;

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Location mapping</h3>
          <InlineHelp label="How location mapping works">
            Incoming host lot IDs are saved after the first match. Name matching is only for lots that are not mapped yet.
            Unmap a wrong first match, then map it again. Unmapped lots do not become deals on the feed owner.
          </InlineHelp>
        </div>
      </div>
      {msg ? <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">{msg}</p> : null}
      {unmatched > 0 && board.subscribers.length > 0 ? (
        <AudienceDataDealerMapCard board={board} mapping={busy} onMap={onMap} />
      ) : unmatched > 0 ? (
        <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          {unmatched} unmatched host lot{unmatched === 1 ? "" : "s"}, but this workspace has no selling organizations to map to.
        </p>
      ) : (
        <p className="text-sm text-gray-600 dark:text-zinc-300">Every host lot with sales in this workspace has a saved location.</p>
      )}
      <div>
        <h4 className="mb-2 text-sm font-semibold text-gray-800 dark:text-zinc-100">Mapped host lots</h4>
        <OpsLocationsMappedList board={board} busy={busy} onRemap={onRemap} onUnmap={onUnmap} />
      </div>
    </div>
  );
}
